import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert } from 'rsuite';
import { AlertWithTimestamp } from '../../../../components/AlertWithTimestamp';
import { extractApiErrorMessage } from '../../../../utils/extractApiErrorMessage';
import { useNetworkPricesStore } from '../NetworkPrices.store';
import { applyNetworkPrices } from '../services';
import { NetworkPriceLine } from '../types';
import { buildApplyPayload, getLineKey } from '../utils';
import { NETWORK_PRICES_QUERY_KEY } from './useNetworkPricesQuery';

type ApplyNetworkPricesActions = {
  applySelected: () => void;
  isApplying: boolean;
  canApply: boolean;
};

export const useApplyNetworkPrices = (lines: NetworkPriceLine[]): ApplyNetworkPricesActions => {
  const queryClient = useQueryClient();
  const selectedLineKeys = useNetworkPricesStore(state => state.selectedLineKeys);
  const draftPrices = useNetworkPricesStore(state => state.draftPrices);
  const pendingCellIds = useNetworkPricesStore(state => state.pendingCellIds);
  const networkDraftPrices = useNetworkPricesStore(state => state.networkDraftPrices);
  const pendingLineKeys = useNetworkPricesStore(state => state.pendingLineKeys);
  const clearSelection = useNetworkPricesStore(state => state.clearSelection);
  const mutation = useMutation({
    mutationFn: applyNetworkPrices,
    onSuccess: () => {
      Alert.success('Precificação salva com sucesso!');
      clearSelection();
      return queryClient.invalidateQueries([NETWORK_PRICES_QUERY_KEY]);
    },
    onError: (error: unknown) => {
      AlertWithTimestamp({ message: extractApiErrorMessage(error) || 'Erro ao salvar precificação!' });
    },
  });
  const selectedKeys = new Set(selectedLineKeys);
  const selectedLines = lines.filter(line => selectedKeys.has(getLineKey(line)));
  const hasSelectedDraft = selectedLines.some(
    line =>
      networkDraftPrices[getLineKey(line)] !== undefined ||
      line.clusters.some(({ productsToBePricedId }) => productsToBePricedId !== null && draftPrices[productsToBePricedId] !== undefined),
  );
  const canApply = selectedLines.length > 0 && !mutation.isLoading && pendingCellIds.length === 0 && pendingLineKeys.length === 0 && !hasSelectedDraft;

  const applySelected = (): void => {
    if (!canApply) return;
    mutation.mutate(buildApplyPayload(selectedLines));
  };

  return { applySelected, isApplying: mutation.isLoading, canApply };
};
